import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import AuthNavbar from "../components/AuthNavbar";
import LanguageSelector from "../components/LanguageSelector";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import { getmessage } from "utils/i18messages";

const EmailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect
      x="2"
      y="4"
      width="20"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M2 6l10 7 10-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function ForgotPassword() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 968);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "en";

    if (!email) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_required"),
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/forgot-password`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message,
        severity: "success",
      });
      setEmailSent(true);
    } catch (error) {
      setLoading(false);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Something went wrong. Try again.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <AuthNavbar />
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -15px); }
        }
      `}</style>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DotPulseLoading size={14} color="#d4a574" />
          </div>
        )}

        {/* Abstract Bubble Background - Left Side (Orange Theme) */}
        <div
          style={{
            position: "absolute",
            left: "-25%",
            top: "40%",
            transform: "translateY(-50%)",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(212, 165, 116, 0.1))",
            }}
          >
            <defs>
              <linearGradient
                id="gradientForgot"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 400,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientForgot)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>

          {/* Smaller floating bubbles */}
          <div
            style={{
              position: "absolute",
              top: "15%",
              right: "10%",
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(232, 207, 179, 0.4), rgba(212, 165, 116, 0.2))",
              animation: "floatSlow 6s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "25%",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(241, 228, 209, 0.5), rgba(232, 207, 179, 0.3))",
              animation: "floatSlow 7s ease-in-out infinite reverse",
            }}
          />

          <div
            style={{
              position: "absolute",
              top: "45%",
              right: "5%",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(250, 246, 241, 0.6), rgba(241, 228, 209, 0.4))",
              animation: "float 5s ease-in-out infinite",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "10%",
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(232, 207, 179, 0.5), rgba(212, 165, 116, 0.3))",
              animation: "floatSlow 6s ease-in-out infinite reverse",
            }}
          />
        </div>

        {/* Main Large Bubble - Right Bottom */}
        <div
          style={{
            position: "absolute",
            right: "-25%",
            bottom: "10%",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(212, 165, 116, 0.1))",
              opacity: 0.2,
            }}
          >
            <defs>
              <linearGradient
                id="gradientForgot2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#faf6f1", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#f1e4d1", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#e8cfb3", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>

            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 900,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientForgot2)"
              style={{
                animation: "float 8s ease-in-out infinite",
              }}
            />
          </svg>
        </div>

        {/* Right Side - Forgot Password Form */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "32px 24px" : "48px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {!emailSent ? (
            /* Form State */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              style={{
                width: "100%",
                maxWidth: "480px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "24px",
                  position: "relative",
                  zIndex: 20,
                }}
              >
                <LanguageSelector />
              </div>
              <h1
                style={{
                  fontSize: isMobile ? "32px" : "40px",
                  fontWeight: "bold",
                  color: "#1a202c",
                  marginBottom: "12px",
                  textAlign: "center",
                }}
              >
                {t("forgotPassword.title")}
              </h1>

              <p
                style={{
                  fontSize: "16px",
                  color: "#718096",
                  textAlign: "center",
                  marginBottom: "40px",
                }}
              >
                {t("forgotPassword.subtitle")}
              </p>

              <form onSubmit={handleSubmit}>
                {/* Email Input */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#4a5568",
                      marginBottom: "8px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("forgotPassword.emailLabel")}
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        fontSize: "15px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        transition: "all 0.3s ease",
                        outline: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#d4a574";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(212, 165, 116, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  style={{
                    width: "100%",
                    padding: "16px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "white",
                    backgroundColor: "#d4a574",
                    border: "none",
                    borderRadius: "8px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                    marginBottom: "24px",
                    fontFamily: "inherit",
                    transition: "all 0.3s ease",
                  }}
                >
                  {loading ? "Sending..." : t("forgotPassword.resetButton")}
                </motion.button>{" "}
              </form>
            </motion.div>
          ) : (
            /* Success State */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              style={{
                width: "100%",
                maxWidth: "480px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  margin: "0 auto 24px",
                  backgroundColor: "#faf6f1",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#d4a574",
                }}
              >
                <EmailIcon />
              </div>
              <h2
                style={{
                  fontSize: isMobile ? "24px" : "32px",
                  fontWeight: "bold",
                  color: "#1a202c",
                  marginBottom: "12px",
                }}
              >
                {t("forgotPassword.success.title")}
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  color: "#718096",
                  marginBottom: "24px",
                }}
              >
                {t("forgotPassword.success.description")}
              </p>
              <div
                style={{
                  backgroundColor: "#faf6f1",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "32px",
                  color: "#d4a574",
                  fontSize: "14px",
                  fontWeight: "500",
                  wordBreak: "break-word",
                }}
              >
                {email}
              </div>{" "}
              <p
                style={{
                  fontSize: "14px",
                  color: "#718096",
                  marginBottom: "32px",
                }}
              >
                {t("forgotPassword.success.message")}
              </p>
              <motion.a
                href="/login"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                style={{
                  display: "inline-block",
                  width: "100%",
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "white",
                  backgroundColor: "#d4a574",
                  border: "none",
                  borderRadius: "8px",
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                {t("forgotPassword.success.loginButton")}
              </motion.a>
            </motion.div>
          )}
        </div>

        {/* Snackbar */}
        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </>
  );
}

export default ForgotPassword;
